"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";

/** Smaller gaps are browser chrome (toolbars collapsing), not a keyboard. */
const MIN_KEYBOARD_PX = 100;
/**
 * A keyboard always leaves some of the screen showing. iPad Safari with a hardware
 * keyboard has reported a keyboard taller than the screen; trusting that sized the
 * console to nothing, so such readings are ignored.
 */
const MIN_VISIBLE_PX = 100;
/** Room left between the line being typed and the top of the keyboard. */
const CARET_MARGIN_PX = 24;
/** A scale this close to 1 is an unzoomed page. */
const ZOOM_TOLERANCE = 0.01;
export const KEYBOARD_INSET_VAR = "--keyboard-inset";
/**
 * Set on <html> while a keyboard is up. Dialogs key their keyboard height cap on it
 * through a variant, which a dialog's own `max-h-*` class can't override.
 */
export const KEYBOARD_OPEN_ATTR = "data-keyboard-open";

/**
 * Keeps the field being typed in above the on-screen keyboard.
 *
 * iPad Safari lays the keyboard over the page without shrinking `100dvh`, and the
 * console scrolls inside its own panes, not the page, so Safari can't scroll a field
 * near the bottom into view and staff type blind. While a keyboard is up this
 * publishes its height as `--keyboard-inset` on <html> (the shell subtracts it from
 * its height), then scrolls the caret of the focused field back into the visible
 * part of the screen. Desktops and Android, where the viewport already shrinks,
 * never see a gap and are left alone.
 */
export function useOnScreenKeyboardInset() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
}
function _temp() {
  const viewport = window.visualViewport;
  if (!viewport) {
    return;
  }
  const root = document.documentElement;
  let frame = null;
  let inset = 0;
  const update = () => {
    frame = null;
    const unzoomed = Math.abs(viewport.scale - 1) < ZOOM_TOLERANCE;
    const typing = isTextEntry(document.activeElement) && unzoomed;
    if (unzoomed && (window.scrollY > 0 || viewport.offsetTop > 0)) {
      window.scrollTo(0, 0);
    }
    const covered = typing ? window.innerHeight - viewport.height : 0;
    const plausible = window.innerHeight - covered >= MIN_VISIBLE_PX;
    const next = covered > MIN_KEYBOARD_PX && plausible ? Math.round(covered) : 0;
    if (next !== inset) {
      inset = next;
      if (inset) {
        root.style.setProperty(KEYBOARD_INSET_VAR, `${inset}px`);
        root.setAttribute(KEYBOARD_OPEN_ATTR, "");
      } else {
        root.style.removeProperty(KEYBOARD_INSET_VAR);
        root.removeAttribute(KEYBOARD_OPEN_ATTR);
      }
    }
    if (inset) {
      revealCaret(viewport);
    }
  };
  const schedule = () => {
    if (frame === null) {
      frame = window.requestAnimationFrame(update);
    }
  };
  viewport.addEventListener("resize", schedule);
  viewport.addEventListener("scroll", schedule);
  document.addEventListener("focusin", schedule);
  document.addEventListener("focusout", schedule);
  document.addEventListener("input", schedule);
  document.addEventListener("selectionchange", schedule);
  return () => {
    viewport.removeEventListener("resize", schedule);
    viewport.removeEventListener("scroll", schedule);
    document.removeEventListener("focusin", schedule);
    document.removeEventListener("focusout", schedule);
    document.removeEventListener("input", schedule);
    document.removeEventListener("selectionchange", schedule);
    if (frame !== null) {
      window.cancelAnimationFrame(frame);
    }
    root.style.removeProperty(KEYBOARD_INSET_VAR);
    root.removeAttribute(KEYBOARD_OPEN_ATTR);
  };
}
function isTextEntry(element: Element | null): element is HTMLElement {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable || element instanceof HTMLTextAreaElement) {
    return true;
  }
  return element instanceof HTMLInputElement && !["button", "checkbox", "radio", "range", "submit", "file"].includes(element.type);
}

/** Copied onto the stand-in for a textarea, so its text wraps the same way. */
const MIRRORED_STYLES = ["boxSizing", "width", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft", "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth", "fontFamily", "fontSize", "fontWeight", "fontStyle", "letterSpacing", "lineHeight", "textTransform", "wordSpacing", "tabSize"] as const;

/**
 * The caret line of a textarea. Browsers don't expose it, so the text up to the caret
 * is laid out in a hidden copy of the box and the end of it measured.
 */
function textareaCaretRect(textarea: HTMLTextAreaElement): DOMRect {
  const box = textarea.getBoundingClientRect();
  const style = window.getComputedStyle(textarea);
  const mirror = document.createElement("div");
  for (const name of MIRRORED_STYLES) mirror.style[name] = style[name];
  Object.assign(mirror.style, {
    position: "absolute",
    visibility: "hidden",
    top: "0",
    left: "-9999px",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    borderStyle: "solid"
  });
  // The end that moves: the start when selecting upward, else the end.
  const caret = textarea.selectionDirection === "backward" ? textarea.selectionStart : textarea.selectionEnd;
  mirror.textContent = textarea.value.slice(0, caret ?? 0);
  const marker = document.createElement("span");
  marker.textContent = "\u200b";
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  const top = box.top + marker.offsetTop - textarea.scrollTop;
  const height = marker.offsetHeight || parseFloat(style.lineHeight) || 20;
  mirror.remove();
  return new DOMRect(box.left, top, box.width, height);
}

/** Where the typing happens: the caret line where it can be found, else the field. */
function caretRect(element: HTMLElement): DOMRect {
  if (element instanceof HTMLTextAreaElement) return textareaCaretRect(element);
  if (element.isContentEditable) {
    // The end of the selection that moves (the focus), which is the start when
    // selecting upward; the whole selection's box could point the other way.
    const selection = window.getSelection();
    const node = selection?.focusNode;
    if (selection && node) {
      const range = document.createRange();
      range.setStart(node, selection.focusOffset);
      range.collapse(true);
      const rect = range.getBoundingClientRect();
      if (rect.height) return rect;
      const holder = node instanceof Element ? node : node.parentElement;
      if (holder) return holder.getBoundingClientRect();
    }
  }
  return element.getBoundingClientRect();
}
function isScrollable(node: HTMLElement): boolean {
  const {
    overflowY
  } = window.getComputedStyle(node);
  return (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
}
function revealCaret(viewport: VisualViewport): void {
  const element = document.activeElement;
  if (!isTextEntry(element)) return;
  const rect = caretRect(element);
  const visibleTop = viewport.offsetTop + CARET_MARGIN_PX;
  const visibleBottom = viewport.offsetTop + viewport.height - CARET_MARGIN_PX;
  let shift = 0;
  if (rect.bottom > visibleBottom) shift = rect.bottom - visibleBottom;else if (rect.top < visibleTop) shift = rect.top - visibleTop;
  // The field itself first (rich-text editors scroll on their own), then each pane
  // around it; whatever one can't absorb goes to the next.
  for (let node: HTMLElement | null = element; node && shift; node = node.parentElement) {
    if (!isScrollable(node)) continue;
    let move = shift;
    if (node === element) {
      // Scrolling inside the field only helps while the caret stays within the part
      // of its box that is on screen; a field under the keyboard leaves it to the panes.
      const box = element.getBoundingClientRect();
      move = shift > 0 ? Math.min(shift, Math.max(0, rect.top - Math.max(box.top, visibleTop))) : Math.max(shift, Math.min(0, rect.bottom - Math.min(box.bottom, visibleBottom)));
    }
    const before = node.scrollTop;
    node.scrollTop = before + move;
    shift -= node.scrollTop - before;
  }
}